import React, { useEffect } from 'react'
import { CloseButton } from 'react-bootstrap'
import { FormProvider, useFieldArray, useForm, useFormContext, useWatch } from 'react-hook-form'

import { Modal } from '../../../components/Modal/Modal'
import { SelectDropdown, type SelectOption } from '../../../components/SelectDropdown/SelectDropdown'
import { TagElement } from '../../../components/Tag/Tag'

import styles from './TagsModal.module.scss'

export interface TagOption {
  id: string
  name: string
}

interface TagsModalFormValues {
  selectedTags: TagOption[]
}

export interface TagsModalProps {
  show: boolean
  onCancel: () => void
  tags: TagOption[]
  loading: boolean
  saving?: boolean
  count: number
  onSave: (selectedTags: TagOption[]) => void
  primaryActionTitle?: string
  notice?: React.ReactNode
  primaryActionDisabled?: boolean
  bodyOverride?: React.ReactNode
  hideSecondaryAction?: boolean
  primaryActionOverride?: () => void
  /** Plural noun used in the header and dropdown placeholder, e.g. 'labels' for Salesforce-family CRMs. */
  entityLabel?: string
}

export const TagsModal: React.FC<TagsModalProps> = ({
  show,
  onCancel,
  tags,
  loading,
  saving,
  count,
  onSave,
  primaryActionTitle = 'Save',
  notice,
  primaryActionDisabled,
  bodyOverride,
  hideSecondaryAction,
  primaryActionOverride,
  entityLabel = 'tags',
}) => {
  const form = useForm<TagsModalFormValues>({ defaultValues: { selectedTags: [] } })
  const { control } = form
  const selectedTags = useWatch({ control, name: 'selectedTags' })

  useEffect(() => {
    if (!show) {
      form.reset({ selectedTags: [] })
    }
  }, [show, form])

  const handleSave = (values: TagsModalFormValues) => {
    if (!values.selectedTags.length) {
      return
    }
    onSave(values.selectedTags)
  }

  const targetLabel = `${count} ${count === 1 ? 'client' : 'clients'}`

  return (
    <Modal
      show={show}
      onHide={onCancel}
      header={
        <h4>
          Assign {entityLabel} to {targetLabel}
        </h4>
      }
      body={
        <FormProvider {...form}>
          {bodyOverride ? bodyOverride : <TagsModalBody tags={tags} loading={loading} notice={notice} entityLabel={entityLabel} />}
        </FormProvider>
      }
      headerBodySeparator={true}
      showFooterActions={true}
      footerClassName='px-4 pb-4 pt-0'
      onFooterPrimaryAction={primaryActionOverride ? primaryActionOverride : form.handleSubmit(handleSave)}
      footerPrimaryActionTitle={primaryActionTitle}
      footerPrimaryActionLoading={saving}
      footerPrimaryActionDisabled={primaryActionOverride ? primaryActionDisabled : !selectedTags.length || primaryActionDisabled}
      onFooterSecondaryAction={onCancel}
      hideSecondaryAction={hideSecondaryAction}
      centered={true}
    />
  )
}

interface TagsModalBodyProps {
  tags: TagOption[]
  loading: boolean
  notice?: React.ReactNode
  entityLabel: string
}

const TagsModalBody: React.FC<TagsModalBodyProps> = ({ tags, loading, notice, entityLabel }) => {
  const { control } = useFormContext<TagsModalFormValues>()
  const { fields: selectedTags, append, remove } = useFieldArray({ control, name: 'selectedTags', keyName: 'fieldId' })

  const tagOptions: SelectOption[] = tags
    .filter((tag) => !selectedTags.some((selected) => selected.id === tag.id))
    .map((tag) => ({ id: tag.id, label: tag.name }))

  const handleOptionSelected = (option: SelectOption) => {
    const tag = tags.find((t) => t.id === option.id)
    if (!tag) {
      return
    }
    append({ id: tag.id, name: tag.name })
  }

  const handleRemoveTag = (id: string) => {
    const index = selectedTags.findIndex((tag) => tag.id === id)
    if (index === -1) {
      return
    }
    remove(index)
  }

  return (
    <div className='d-flex flex-column gap-4'>
      <div className='paragraph-medium'>This will run in the background. Feel free to close this window or continue with other tasks.</div>
      <SelectDropdown
        className='py-2 zocks-px-0.75 zocks-gap-3'
        options={tagOptions}
        initialSelectedOption={null}
        onOptionSelected={handleOptionSelected}
        placeholder={`Select ${entityLabel} to assign`}
        clearDropdownOnOptionSelected={true}
        loading={loading}
        searchable
      />
      {selectedTags.length ? (
        <div className='d-flex align-items-center gap-2 flex-wrap'>
          {selectedTags.map((tag) => (
            <TagElement key={tag.id} className='gap-2'>
              <i className='bi bi-tag' />
              <span className='paragraph-small medium'>{tag.name}</span>{' '}
              <CloseButton className={styles.tagCloseButton} onClick={() => handleRemoveTag(tag.id)} />
            </TagElement>
          ))}
        </div>
      ) : null}
      {notice ? notice : null}
    </div>
  )
}
